'use client';

import { LingDatabaseSDK } from '@/sharedModules/databaseSDK';
import { getIdToken } from '@/features/services/firebase/client/auth';
import { useAppDispatch, useAppSelector } from '@/features/common/store/hooks';
import { useEffect, useCallback } from 'react';
import { FirestoreChangeNotification } from '@/sharedModules/databaseSDK/types/api';
import {
  setUserProfileAndSettings,
  UserState,
  setSingleLanguageProgress,
} from '@/features/profile/slices/userSlice';
import { createLanguageProgressItem } from '@/features/profile/utilities/progressUtilities';

export const sdk = LingDatabaseSDK.initialize({
  environment:
    process.env.NEXT_PUBLIC_STAGE === 'production' ? 'production' : 'test',
  getAuthToken: () => getIdToken(true),
});

export const DatabaseSdkService = () => {
  const isUserLoggedIn = useAppSelector(state => state.auth.isUserLoggedIn);
  const userProfile = useAppSelector(state => state.user.profile);
  const dispatch = useAppDispatch();

  const handleFirestoreChange = useCallback(
    (change: FirestoreChangeNotification) => {
      try {
        const { event, change: docChange } = change.payload;

        if (event.params.uid !== userProfile.uid) {
          return;
        }

        if (
          event.document === `users/${userProfile.uid}` &&
          docChange.changeType === 'UPDATED'
        ) {
          if (docChange.after) {
            const userData = docChange.after as unknown as UserState;
            const profileData = userData?.profile;
            const settingsData = userData?.settings;

            dispatch(
              setUserProfileAndSettings({
                profile: profileData,
                settings: settingsData,
              }),
            );
          }
        }

        // Handle language progress changes (banana points, weekly graph, etc.)
        const languageProgressMatch = event.document.match(
          /^users\/[^/]+\/languageProgress\/(.+)$/,
        );
        if (languageProgressMatch && docChange.changeType === 'UPDATED') {
          if (docChange.after) {
            const targetLanguage = languageProgressMatch[1];
            const progressData = docChange.after as any;

            const progressToDispatch = createLanguageProgressItem(progressData);
            dispatch(
              setSingleLanguageProgress({
                targetLanguage,
                progress: progressToDispatch,
              }),
            );
          }
        }
      } catch (error) {
        console.error('❌ Error processing real-time change:', error);
      }
    },
    [dispatch, userProfile.uid],
  );

  useEffect(() => {
    let listenerId: string = '';

    if (isUserLoggedIn) {
      sdk.websocket
        .connect()
        .then(() => {
          listenerId = sdk.websocket.addListener(
            change => {
              handleFirestoreChange(change);
            },
            {
              onConnection: message => {
                console.log('🚀 WebSocket connection established:', message);
              },
              onDisconnect: () => {
                console.log('❌ WebSocket disconnected');
              },
              onError: error => {
                console.error('💥 WebSocket error:', error);
              },
            },
          );
        })
        .catch(error => {
          console.error('🚫 Failed to connect to WebSocket:', error);
        });

      return () => {
        if (listenerId) {
          sdk.websocket.removeListener(listenerId);
        }
      };
    }
  }, [isUserLoggedIn, userProfile.uid, handleFirestoreChange]);

  return null;
};
